import CrudModel from './CrudModel';
import { DECIMAL_TYPES, roundDecimalByConfig } from 'utils/decimal';
import { getBackendBoolean } from 'utils/fm';
import { fromExtraFieldsEndpoint, fromExtraFields, toNumber, toDecimal } from 'utils/crud';
import { getDateMomentFromBackendTimestamp } from 'utils/dates';
import { getLiteral } from '../utils/getLiteral';

/** Stock / recommended quantity: backend uses -1 (string or number) to mean unset (UI "-", CRUD vacío). */
export const isUnsetProductQuantity = (value) => {
    if (value === '-1' || value === -1) return true;
    if (value === null || value === undefined || value === '') return true;
    const n = Number(String(value)?.replace(',', '.'));
    return !Number.isNaN(n) && n === -1;
};

const getProductEnvironmentIds = (value) => {
    if (!value) return undefined;
    const ids = String(value).split(/[,;]/).filter(Boolean);
    return ids.length > 0 ? ids : undefined;
};

export default class ProductModel extends CrudModel {
    constructor(entity) {
        super(entity);
    }

    static toList({ data }) {
        return data?.map((item) => ({ id: item.Id, product: item }));
    }

    static standardToCrud(data) {
        const strIdEnvironments = [];
        const productEnvironments = getProductEnvironmentIds(data.IdEnvironments);
        const idCurrency = data.IdCurrency !== '-1' ? data.IdCurrency : undefined;
        if (data.IdEnvironments?.length > 0) {
            const ids = data.IdEnvironments.split(';');
            ids.pop();
            const names = data.NameEnvironmentschan.split(';');
            names.pop();

            ids.forEach((id, index) => {
                if (ids.length === index) return false;
                strIdEnvironments.push({
                    label: names[index],
                    value: id,
                });
            });
        }

        return {
            id: toNumber(data.Id),
            modelo: {
                modelo: data.ProductModel,
                modelo_us: data.ProductModel_US,
                modelo_de: data.ProductModel_DE,
                modelo_en: data.ProductModel_EN,
                modelo_fr: data.ProductModel_FR,
                modelo_it: data.ProductModel_IT,
                modelo_pt: data.ProductModel_PT,
                modelo_ru: data.ProductModel_RU,
                modelo_br: data.ProductModel_BR,
            },
            descripcion: {
                descripcion: data.ProductDescription,
                descripcion_us: data.ProductDescription_US,
                descripcion_de: data.ProductDescription_DE,
                descripcion_en: data.ProductDescription_EN,
                descripcion_fr: data.ProductDescription_FR,
                descripcion_it: data.ProductDescription_IT,
                descripcion_pt: data.ProductDescription_PT,
                descripcion_ru: data.ProductDescription_RU,
                descripcion_br: data.ProductDescription_BR,
            },
            strIdEnvironments: data.IdEnvironments?.split(','),
            precio: roundDecimalByConfig({
                fieldId: 'precio',
                idCurrency,
                idEnvironment: productEnvironments,
                entityName: 'PRODUCTO',
                type: DECIMAL_TYPES.CURRENCY_PRICES,
                value: data.ProductPrice,
            }),
            coste: roundDecimalByConfig({
                fieldId: 'preciocoste',
                idCurrency,
                idEnvironment: productEnvironments,
                entityName: 'PRODUCTO',
                type: DECIMAL_TYPES.CURRENCY_PRICES,
                value: data.ProductCost,
            }),
            descmax: toDecimal(data.ProductMaxDiscount || 0),
            stock: !isUnsetProductQuantity(data.Stock)
                ? roundDecimalByConfig({
                      fieldId: 'stock',
                      idEnvironment: productEnvironments,
                      entityName: 'PRODUCTO',
                      type: DECIMAL_TYPES.QUANTITY,
                      value: data.Stock,
                  })
                : undefined,
            recommendedQuantity: !isUnsetProductQuantity(data.RecommendedQuantity)
                ? roundDecimalByConfig({
                      fieldId: 'recommendedQuantity',
                      idEnvironment: productEnvironments,
                      entityName: 'PRODUCTO',
                      type: DECIMAL_TYPES.QUANTITY,
                      value: data.RecommendedQuantity,
                  })
                : undefined,
            idfamilia:
                data.ProductFamilyId !== '-1'
                    ? {
                          label: data.ProductFamily,
                          value: data.ProductFamilyId,
                      }
                    : {
                          label: getLiteral('title_products'),
                          value: '-1',
                      },
            idtipo:
                data.ProductCategoryId !== '-1'
                    ? {
                          label: data.ProductCategory,
                          value: data.ProductCategoryId,
                      }
                    : undefined,
            available: getBackendBoolean(data.Available),
            createdDate: getDateMomentFromBackendTimestamp(data.Created_timestamp),
            defaultTaxRateId: !!data.defaultTaxRateId
                ? {
                      label: data.defaultTaxRate,
                      value: data.defaultTaxRateId,
                  }
                : undefined,
        };
    }

    static toCrud({ data, mappedSchema }) {
        const baseData = this.standardToCrud(data);
        const extraFields =
            !data.Extrafields.ExtraFieldUnit && data.extraFields
                ? fromExtraFieldsEndpoint(data.extraFields || [], mappedSchema)
                : fromExtraFields(
                      data.Extrafields && data.Extrafields.ExtraFieldUnit
                          ? data.Extrafields.ExtraFieldUnit
                          : [],
                      mappedSchema,
                  );

        return { ...baseData, ...extraFields };
    }

    get key() {
        return this.id + '' + this.isFolder;
    }

    get id() {
        return this.entity.id;
    }

    get name() {
        return this.entity.name;
    }

    get description() {
        return this.entity.description;
    }

    get isFolder() {
        return this.entity.isproductfolder === 1;
    }

    get price() {
        return this.entity.finalprice;
    }

    get thumbnail() {
        return this.entity.idthumbnail;
    }

    get stock() {
        return this.entity.stock;
    }

    get recommendedQuantity() {
        return this.entity.recommendedquantity;
    }

    get comments() {
        return this.entity.comments;
    }

    get pathid() {
        return this.entity.pathid;
    }

    get path() {
        return this.entity.path;
    }

    getDiscount(num) {
        return this.entity['discount' + num] ? this.entity['discount' + num] : 0;
    }
}
